import * as React from 'react';
import { useConfigApp, useConfigAppPath } from '@web-config-app/core-react';
import type { EnvironmentKey, ResolvedModule } from '@web-config-app/core';

type ModulePath = { tabId: string; moduleId?: string; childModuleId?: string };

/**
 * Traverses the module tree once and records the URL-path coordinates of
 * every entity node keyed by entity ID. Depth is capped at three by the
 * generation pipeline, so the recursion is bounded.
 */
const buildEntityIdPathMap = (
  modules: ResolvedModule[],
): Map<string, ModulePath> => {
  const map = new Map<string, ModulePath>();
  modules.forEach((tab) => {
    if (tab.type === 'entity') {
      map.set(tab.id, { tabId: tab.id });
    }
    (tab.modules ?? []).forEach((child) => {
      if (child.type === 'entity') {
        map.set(child.id, { tabId: tab.id, moduleId: child.id });
      }
      (child.modules ?? []).forEach((grandchild) => {
        if (grandchild.type === 'entity') {
          map.set(grandchild.id, {
            tabId: tab.id,
            moduleId: child.id,
            childModuleId: grandchild.id,
          });
        }
      });
    });
  });
  return map;
};

export const useGetEntityDetailPath = () => {
  const { entityConfigs, modules = [] } = useConfigApp();
  const generateConfigPaths = useConfigAppPath();
  const { getEntityDetailsPathForModuleEnvAndId } = generateConfigPaths();

  // Single tree walk to build entityId → path, then rekey by entity type
  // via entityConfigs.
  const modulePathByEntityType = React.useMemo(() => {
    const pathByEntityId = buildEntityIdPathMap(modules);
    const map = new Map<string, ModulePath>();
    Object.entries(entityConfigs).forEach(([entityId, config]) => {
      const path = pathByEntityId.get(entityId);
      if (path) map.set(config.type, path);
    });
    return map;
  }, [modules, entityConfigs]);

  return React.useCallback(
    (
      entityType: string,
      instanceId: string,
      environment?: EnvironmentKey,
    ): string | undefined => {
      const modulePath = modulePathByEntityType.get(entityType);
      if (!modulePath || !environment) return undefined;

      return getEntityDetailsPathForModuleEnvAndId(
        modulePath.tabId,
        modulePath.moduleId,
        instanceId,
        environment,
        modulePath.childModuleId,
      );
    },
    [modulePathByEntityType, getEntityDetailsPathForModuleEnvAndId],
  );
};
